import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { PermissionService } from 'src/app/core/services/permission-service.service';
import { HrserviceService } from 'src/app/components/hr-manager/service/hrservice.service';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp, Firestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, FirebaseStorage } from 'firebase/storage';
import { getApp } from 'firebase/app';

interface EmployeeInfo {
  name: string;
  code: string;
  email: string;
  contact: string;
  department: string;
  gender: string;
  company: string;
  category: string;
}

interface OffsiteConfig {
  form_active: boolean;
  form_deadline: Date | null;
  form_location: string;
  offsite_start: Date | null;
  offsite_end: Date | null;
  double_sharing_amount: number | null;
  single_sharing_amount: number | null;
  offsite_year: string;
  banner_image_url: string;
}

@Component({
  selector: 'app-offsite-form',
  templateUrl: './offsite-form.component.html',
  styleUrls: ['./offsite-form.component.scss']
})
export class OffsiteFormComponent implements OnInit, OnDestroy {
  offsiteForm!: FormGroup;
  showAreaB = false;
  showAreaC = false;
  showAreaD = false;
  
  // Active section tracking
  activeSection: string = 'attendance';
  currentStep: number = 1;
  
  private readonly destroy$ = new Subject<void>();
  
  // Employee information (auto-populated from API)
  employeeInfo: EmployeeInfo = {
    name: '',
    code: '',
    email: '',
    contact: '',
    department: '',
    gender: '',
    company: '',
    category: ''
  };

  isLoadingEmployeeData = true;

  // Loader for Firestore form data
  isLoadingFormData = false;

  // City list
  cities = [
    // Delhi
    'New Delhi',
    // Maharashtra
    'Mumbai',
    'Pune',
    'Aurangabad',
    'Nagpur',
    // Karnataka
    'Bangalore',
    // Gujarat
    'Ahmedabad',
    // West Bengal
    'Kolkata',
    // Odisha
    'Bhubaneswar',
    // Telangana
    'Hyderabad',
    // Uttar Pradesh
    'Lucknow',
    'Varanasi',
    // Tamil Nadu
    'Tuticorin',
    'Madurai',
    'Coimbatore',
    // Madhya Pradesh
    'Gwalior',
    'Bhopal',
    // Rajasthan
    'Jaipur',
    'Jodhpur',
    'Bikaner',
    'Jaisalmer',
    // Chhattisgarh
    'Raipur',
    // Punjab/Haryana
    'Chandigarh',
    'Other'
  ];

  uploadedFiles: { file: File | null; displayName: string; downloadURL?: string; uploading?: boolean; previewURL?: string; fileType?: string }[] = [];

  // Offsite config from Firestore
  offsiteConfig: OffsiteConfig = {
    form_active: false,
    form_deadline: null,
    form_location: '',
    offsite_start: null,
    offsite_end: null,
    double_sharing_amount: null,
    single_sharing_amount: null,
    offsite_year: '',
    banner_image_url: ''
  };
  isLoadingConfig = true;

  // Deadline countdown timer
  deadlineDate: Date = new Date('2026-03-05T23:59:59');
  timerDisplay = '';
  timerUrgent = false;
  timerExpired = false;
  deadlineDateLabel = '';
  private timerInterval: any;

  isSaving = false;
  isSubmitting = false;
  isSubmitted = false;
  isUploading = false;
  firestoreDocId = '';
  private isResolvingCompanyName = false;

  private db!: Firestore;
  private storage!: FirebaseStorage;

  get isFormReadOnly(): boolean {
    return this.isSubmitted || this.timerExpired;
  }

  get sectionComplete(): { attendance: boolean; preferences: boolean; accommodation: boolean; travel: boolean } {
    const f = this.offsiteForm;
    const attendance = !!f?.get('attendance')?.value;
    const aadharVal = f?.get('aadharNumber')?.value || '';
    const validAadhar = /^\d{4} \d{4} \d{4}$/.test(aadharVal);
    const preferences = this.showAreaB &&
      !!f?.get('mealPreference')?.value &&
      validAadhar &&
      this.uploadedFiles.length > 0;
    const accommodation = this.showAreaC && !!f?.get('roomAllocation')?.value;
    const departureOk = f?.get('departureCity')?.value === 'Other'
      ? !!f?.get('departureCityOther')?.value
      : !!f?.get('departureCity')?.value;
    const returnOk = f?.get('returnCity')?.value === 'Other'
      ? !!f?.get('returnCityOther')?.value
      : !!f?.get('returnCity')?.value;
    const travel = this.showAreaD && departureOk && returnOk;
    return { attendance, preferences, accommodation, travel };
  }

  get completedSections(): number {
    const s = this.sectionComplete;
    return [s.attendance, s.preferences, s.accommodation, s.travel].filter(Boolean).length;
  }

  get progressPercent(): number {
    if (this.offsiteForm?.get('attendance')?.value === 'No') return 100;
    const total = this.showAreaB ? 4 : 1;
    return Math.round((this.completedSections / total) * 100);
  }

  get isGoaTheme(): boolean {
    return (this.offsiteConfig?.form_location || '').toLowerCase().includes('goa');
  }

  get destinationCity(): string {
    return this.offsiteConfig?.form_location?.trim() || 'the offsite city';
  }

  get doubleShareAmountDisplay(): string {
    const amt = this.offsiteConfig?.double_sharing_amount;
    if (!amt) return '';

    const start = this.offsiteConfig?.offsite_start;
    const end = this.offsiteConfig?.offsite_end;
    let nightsSuffix = '';
    if (start && end) {
      const nights = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (nights > 0) {
        nightsSuffix = ` (for ${nights} night${nights !== 1 ? 's' : ''})`;
      }
    }

    return `INR ${amt.toLocaleString('en-IN')} per person${nightsSuffix}`;
  }

  get singleShareAmountDisplay(): string {
    const amt = this.offsiteConfig?.single_sharing_amount;
    if (!amt) return '';

    const start = this.offsiteConfig?.offsite_start;
    const end = this.offsiteConfig?.offsite_end;
    let nightsSuffix = '';
    if (start && end) {
      const nights = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (nights > 0) {
        nightsSuffix = ` (for ${nights} night${nights !== 1 ? 's' : ''})`;
      }
    }

    return `INR ${amt.toLocaleString('en-IN')} per person${nightsSuffix}`;
  }

  get showDoubleSharingOption(): boolean {
    return this.hasPositiveAmount(this.offsiteConfig?.double_sharing_amount);
  }

  get showSingleSharingOption(): boolean {
    return this.hasPositiveAmount(this.offsiteConfig?.single_sharing_amount);
  }

  get availableRoomTypesDisplay(): string {
    const roomTypes = ['Triple'];
    if (this.showDoubleSharingOption) roomTypes.push('Double');
    if (this.showSingleSharingOption) roomTypes.push('Single');

    return roomTypes.length === 2 ? `${roomTypes[0]} or ${roomTypes[1]}` : roomTypes.join(', ');
  }

  get paidSharingTypesDisplay(): string {
    const roomTypes = [];
    if (this.showDoubleSharingOption) roomTypes.push('Double');
    if (this.showSingleSharingOption) roomTypes.push('Single');

    return roomTypes.join('/');
  }

  get offsiteYear(): string {
    return this.offsiteConfig?.offsite_year || new Date().getFullYear().toString();
  }

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private permissionService: PermissionService,
    private hrService: HrserviceService
  ) { }

  ngOnInit(): void {
    // Initialize Firebase services from the already-initialized app
    const app = getApp();
    this.db = getFirestore(app);
    this.storage = getStorage(app);

    this.initializeForm();
    this.loadEmployeeData();
    this.isLoadingFormData = true;
    this.loadSavedData();
    this.loadConfig();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  loadConfig(): void {
    const currentYear = new Date().getFullYear().toString();
    const configRef = doc(this.db, 'config', currentYear);
    getDoc(configRef)
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          this.offsiteConfig = {
            form_active: data['form_active'] === true,
            form_deadline: data['form_deadline']?.toDate?.() ?? null,
            form_location: data['form_location'] || '',
            offsite_start: data['offsite_start']?.toDate?.() ?? null,
            offsite_end: data['offsite_end']?.toDate?.() ?? null,
            double_sharing_amount: data['double_sharing_amount'] ?? null,
            single_sharing_amount: data['single_sharing_amount'] ?? null,
            offsite_year: data['offsite_year'] || '',
            banner_image_url: data['banner_image_url'] || ''
          };
          if (this.offsiteConfig.form_deadline) {
            this.deadlineDate = this.offsiteConfig.form_deadline;
          }
        }
        this.resetUnavailableRoomAllocation();
        this.isLoadingConfig = false;
        this.startDeadlineTimer();
      })
      .catch((err) => {
        console.error('Failed to load offsite config:', err);
        this.isLoadingConfig = false;
        this.startDeadlineTimer(); // fall back to hardcoded date
      });
  }

  formatConfigDate(date: Date | null): string {
    if (!date) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  startDeadlineTimer(): void {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = this.deadlineDate;
    this.deadlineDateLabel = `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;

    const tick = () => {
      const now = new Date().getTime();
      const diff = this.deadlineDate.getTime() - now;
      if (diff <= 0) {
        this.markDeadlinePassed();
        clearInterval(this.timerInterval);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      this.timerDisplay = `${String(days).padStart(2, '0')}d `
        + `${String(hours).padStart(2, '0')}h `
        + `${String(minutes).padStart(2, '0')}m `
        + `${String(seconds).padStart(2, '0')}s`;
      this.timerUrgent = days < 3;
    };

    tick();
    this.timerInterval = setInterval(tick, 1000);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.offsiteForm.get('attendance')?.value === 'Yes') {
      this.updateActiveSection();
    }
  }

  updateActiveSection(): void {
    const sections = [
      { id: 'area-a', name: 'attendance', step: 1 },
      { id: 'area-b', name: 'preferences', step: 2 },
      { id: 'area-c', name: 'accommodation', step: 3 },
      { id: 'area-d', name: 'travel', step: 4 }
    ];

    // Check if scrolled to bottom of page — activate last visible section
    const scrolledToBottom = (window.innerHeight + window.scrollY) >= (document.body.scrollHeight - 100);
    if (scrolledToBottom) {
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element) {
          this.activeSection = sections[i].name;
          this.currentStep = sections[i].step;
          return;
        }
      }
    }

    // Switch when the section-header bottom edge (divider line) crosses the sticky header
    for (let i = sections.length - 1; i >= 0; i--) {
      const element = document.getElementById(sections[i].id);
      if (element) {
        const sectionHeader = element.querySelector('.section-header');
        if (sectionHeader) {
          const headerBottom = sectionHeader.getBoundingClientRect().bottom;
          // Activate when the divider below the section header scrolls past the sticky header
          if (headerBottom <= 20) {
            this.activeSection = sections[i].name;
            this.currentStep = sections[i].step;
            return;
          }
        }
        // Fallback: if section top is above viewport, it's at least partially active
        const rect = element.getBoundingClientRect();
        if (rect.top <= 0) {
          this.activeSection = sections[i].name;
          this.currentStep = sections[i].step;
          return;
        }
      }
    }
  }

  scrollToSection(sectionId: string): void {
    const sectionMap: { [key: string]: { name: string; step: number } } = {
      'area-a': { name: 'attendance', step: 1 },
      'area-b': { name: 'preferences', step: 2 },
      'area-c': { name: 'accommodation', step: 3 },
      'area-d': { name: 'travel', step: 4 }
    };

    if (sectionMap[sectionId]) {
      this.activeSection = sectionMap[sectionId].name;
      this.currentStep = sectionMap[sectionId].step;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 110;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' });
    }
  }

  loadEmployeeData(): void {
    // Try to load from localStorage first
    const storedData = localStorage.getItem('empdata');
    
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        const employeeData = this.buildEmployeeInfo(data);
        this.employeeInfo = employeeData.info;
        this.resolveCompanyName(employeeData.companyIdentifier);
        this.isLoadingEmployeeData = false;
        console.log('Employee data loaded from localStorage:', this.employeeInfo);
      } catch (error) {
        console.error('Error parsing employee data from localStorage:', error);
        this.loadFromService();
      }
    } else {
      // Fallback to service if localStorage is empty
      this.loadFromService();
    }
  }

  loadFromService(): void {
    this.permissionService.getEmployeeData()
      .pipe(
        filter(data => !!data),
        take(1)
      )
      .subscribe({
        next: (data) => {
          if (data) {
            const employeeData = this.buildEmployeeInfo(data);
            this.employeeInfo = employeeData.info;
            this.resolveCompanyName(employeeData.companyIdentifier);
            this.isLoadingEmployeeData = false;
            console.log('Employee data loaded from service:', this.employeeInfo);
          }
        },
        error: (error) => {
          console.error('Error loading employee data:', error);
          this.isLoadingEmployeeData = false;
          this.toastr.error('Failed to load employee information. Please refresh the page.');
        }
      });
  }

  private buildEmployeeInfo(data: any): { info: EmployeeInfo; companyIdentifier: string } {
    const companyName = this.extractCompanyName(data);
    const companyIdentifier = this.extractCompanyIdentifier(data);

    return {
      info: {
        name: data.name || '',
        code: data.emp_id || data.emp_code || data.employee_code || '',
        email: data.email || '',
        contact: data.mobile_no || data.contact || data.phone || '',
        department: data.department || '',
        gender: data.gender || '',
        company: companyName,
        category: data.employee_category || data.category || this.getEmployeeCategory(data)
      },
      companyIdentifier
    };
  }

  private extractCompanyName(data: any): string {
    const company = data?.company;

    return this.firstNonEmptyString(
      data?.company_name,
      data?.companyName,
      data?.company__name,
      data?.company_display,
      data?.companyDisplay,
      data?.company_display_name,
      data?.companyDisplayName,
      data?.company_full_name,
      data?.companyFullName,
      company?.name,
      company?.company_name,
      company?.companyName,
      company?.label
    );
  }

  private extractCompanyIdentifier(data: any): string {
    const company = data?.company;

    if (typeof company === 'string' || typeof company === 'number') {
      return String(company).trim();
    }

    return this.firstNonEmptyString(
      data?.company_id,
      data?.companyId,
      data?.company_code,
      data?.companyCode,
      company?.company_id,
      company?.companyId,
      company?.id,
      company?.code
    );
  }

  private resolveCompanyName(companyIdentifier: string): void {
    if (!companyIdentifier || this.employeeInfo.company || this.isResolvingCompanyName) return;

    this.isResolvingCompanyName = true;
    const normalizedIdentifier = this.normalizeLookupValue(companyIdentifier);

    this.hrService.getCompanies().pipe(take(1)).subscribe({
      next: (res: any) => {
        const companies = res?.data?.companies || res?.companies || [];
        const matchedCompany = companies.find((company: any) => {
          const candidates = [
            company?.company_id,
            company?.companyId,
            company?.id,
            company?.code
          ];
          return candidates.some(value => this.normalizeLookupValue(value) === normalizedIdentifier);
        });

        const companyName = this.firstNonEmptyString(
          matchedCompany?.name,
          matchedCompany?.company_name,
          matchedCompany?.companyName
        );

        if (companyName) {
          this.employeeInfo = { ...this.employeeInfo, company: companyName };
        }

        this.isResolvingCompanyName = false;
      },
      error: (error) => {
        console.warn('Unable to resolve company name from company list:', error);
        this.isResolvingCompanyName = false;
      }
    });
  }

  private firstNonEmptyString(...values: any[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return '';
  }

  private normalizeLookupValue(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  getEmployeeCategory(data: any): string {
    // Determine employee category based on available data
    if (data.is_on_roll || data.employment_type === 'on_roll') {
      return 'On roll';
    } else if (data.is_offroll_ho || data.location === 'Gurgaon') {
      return 'Offroll (HO)';
    } else if (data.is_offroll) {
      return 'Offroll (>1 year)';
    }
    return 'On roll'; // Default
  }

  initializeForm(): void {
    this.offsiteForm = this.fb.group({
      // Area A
      attendance: ['', Validators.required],

      // Area B
      mealPreference: [''],
      aadharNumber: [''],
      idDocument: [null],
      
      // Area C
      roomAllocation: ['triple'],
      
      // Area D
      departureCity: [''],
      departureCityOther: [''],
      returnCity: [''],
      returnCityOther: ['']
    });

    // Watch attendance change
    this.offsiteForm.get('attendance')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.onAttendanceChange(value);
    });

    // Watch city selection changes
    this.offsiteForm.get('departureCity')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value === 'Other') {
        this.offsiteForm.get('departureCityOther')?.setValidators([Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)]);
      } else {
        this.offsiteForm.get('departureCityOther')?.clearValidators();
        this.offsiteForm.get('departureCityOther')?.setValue('');
      }
      this.offsiteForm.get('departureCityOther')?.updateValueAndValidity();
    });

    this.offsiteForm.get('returnCity')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value === 'Other') {
        this.offsiteForm.get('returnCityOther')?.setValidators([Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)]);
      } else {
        this.offsiteForm.get('returnCityOther')?.clearValidators();
        this.offsiteForm.get('returnCityOther')?.setValue('');
      }
      this.offsiteForm.get('returnCityOther')?.updateValueAndValidity();
    });
  }

  onAttendanceChange(value: string): void {
    if (value === 'Yes') {
      this.showAreaB = true;
      this.showAreaC = true;
      this.showAreaD = true;

      // Set validators for conditional fields
      this.offsiteForm.get('mealPreference')?.setValidators(Validators.required);
      this.offsiteForm.get('aadharNumber')?.setValidators([Validators.required, Validators.pattern(/^\d{4} \d{4} \d{4}$/)]);
      this.offsiteForm.get('idDocument')?.setValidators(Validators.required);
      this.offsiteForm.get('roomAllocation')?.setValidators(Validators.required);
      this.offsiteForm.get('departureCity')?.setValidators(Validators.required);
      this.offsiteForm.get('returnCity')?.setValidators(Validators.required);
    } else if (value === 'No') {
      this.showAreaB = false;
      this.showAreaC = false;
      this.showAreaD = false;

      // Clear validators for conditional fields
      this.offsiteForm.get('mealPreference')?.clearValidators();
      this.offsiteForm.get('aadharNumber')?.clearValidators();
      this.offsiteForm.get('idDocument')?.clearValidators();
      this.offsiteForm.get('roomAllocation')?.clearValidators();
      this.offsiteForm.get('departureCity')?.clearValidators();
      this.offsiteForm.get('returnCity')?.clearValidators();
      this.offsiteForm.get('departureCityOther')?.clearValidators();
      this.offsiteForm.get('returnCityOther')?.clearValidators();
    }

    // Update validity
    this.offsiteForm.get('mealPreference')?.updateValueAndValidity();
    this.offsiteForm.get('aadharNumber')?.updateValueAndValidity();
    this.offsiteForm.get('idDocument')?.updateValueAndValidity();
    this.offsiteForm.get('roomAllocation')?.updateValueAndValidity();
    this.offsiteForm.get('departureCity')?.updateValueAndValidity();
    this.offsiteForm.get('returnCity')?.updateValueAndValidity();
  }

  onFileSelect(event: any): void {
    if (this.isFormReadOnly) {
      event.target.value = '';
      this.showReadOnlyWarning();
      return;
    }

    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const currentYear = new Date().getFullYear().toString();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!allowedTypes.includes(file.type)) {
        this.toastr.error(`"${file.name}" — Only JPEG, PNG, or PDF files are allowed`);
        continue;
      }

      if (file.size > maxSize) {
        this.toastr.error(`"${file.name}" — File size must not exceed 5MB`);
        continue;
      }

    const aadharSuffix = (this.offsiteForm.get('aadharNumber')?.value || '').replace(/\s/g, '').slice(-4) || 'XXXX';
      const ext = file.name.split('.').pop();
      const displayName = `${this.employeeInfo.code}_${this.employeeInfo.name.replace(/\s+/g, '_')}_Aadhar_${aadharSuffix}_${this.uploadedFiles.length + 1}.${ext}`;

      // Add to list immediately with uploading state + preview
      const fileType = file.type.startsWith('image/') ? 'image' : 'pdf';
      const fileEntry: typeof this.uploadedFiles[0] = { file, displayName, uploading: true, downloadURL: '', fileType, previewURL: '' };
      this.uploadedFiles.push(fileEntry);
      this.isUploading = true;

      // Generate local preview for images
      if (fileType === 'image') {
        const reader = new FileReader();
        reader.onload = (e: any) => { fileEntry.previewURL = e.target.result; };
        reader.readAsDataURL(file);
      }

      // Upload to Firebase Storage immediately
      const storagePath = `offsite/${currentYear}/${this.employeeInfo.code}/submission/${displayName}`;
      const storageRef = ref(this.storage, storagePath);

      uploadBytes(storageRef, file)
        .then(snapshot => getDownloadURL(snapshot.ref))
        .then(url => {
          fileEntry.downloadURL = url;
          fileEntry.uploading = false;
          this.isUploading = this.uploadedFiles.some(f => f.uploading);
          this.offsiteForm.patchValue({
            idDocument: this.uploadedFiles.filter(f => f.downloadURL).map(f => f.displayName)
          });
          this.toastr.success(`"${displayName}" uploaded successfully`);
        })
        .catch(err => {
          console.error('File upload error:', err);
          fileEntry.uploading = false;
          this.isUploading = this.uploadedFiles.some(f => f.uploading);
          this.toastr.error(`Failed to upload "${file.name}". Please try again.`);
          // Remove failed file
          const idx = this.uploadedFiles.indexOf(fileEntry);
          if (idx > -1) this.uploadedFiles.splice(idx, 1);
        });
    }

    // Reset input so same file can be re-selected
    event.target.value = '';
  }

  isIndianCitizen(): boolean {
    return true;
  }

  removeFile(index: number): void {
    if (this.isFormReadOnly) {
      this.showReadOnlyWarning();
      return;
    }

    this.uploadedFiles.splice(index, 1);
    this.offsiteForm.patchValue({
      idDocument: this.uploadedFiles.length > 0 ? this.uploadedFiles.map(f => f.displayName) : null
    });
  }

  saveMidway(): void {
    if (this.isFormReadOnly) {
      this.showReadOnlyWarning();
      return;
    }

    this.isSaving = true;
    this.firestoreDocId = this.employeeInfo.code || 'unknown';
    const currentYear = new Date().getFullYear().toString();

    const payload: Record<string, any> = {
      // Employee info
      employeeCode: this.employeeInfo.code,
      employeeName: this.employeeInfo.name,
      employeeEmail: this.employeeInfo.email,
      employeeContact: this.employeeInfo.contact,
      employeeDepartment: this.employeeInfo.department,
      employeeGender: this.employeeInfo.gender,
      employeeCompany: this.employeeInfo.company,
      employeeCategory: this.employeeInfo.category,

      // Form fields
      attendance: this.offsiteForm.value.attendance || '',
      mealPreference: this.offsiteForm.value.mealPreference || '',
      aadharNumber: this.offsiteForm.value.aadharNumber || '',
      roomAllocation: this.offsiteForm.value.roomAllocation || '',
      departureCity: this.offsiteForm.value.departureCity === 'Other'
        ? this.offsiteForm.value.departureCityOther || ''
        : this.offsiteForm.value.departureCity || '',
      returnCity: this.offsiteForm.value.returnCity === 'Other'
        ? this.offsiteForm.value.returnCityOther || ''
        : this.offsiteForm.value.returnCity || '',
      departureCityRaw: this.offsiteForm.value.departureCity || '',
      departureCityOther: this.offsiteForm.value.departureCityOther || '',
      returnCityRaw: this.offsiteForm.value.returnCity || '',
      returnCityOther: this.offsiteForm.value.returnCityOther || '',

      // File references (names only for draft)
      uploadedFileNames: this.uploadedFiles.map(f => f.displayName),
      uploadedFileURLs: this.uploadedFiles.filter(f => f.downloadURL).map(f => f.downloadURL),

      // Status
      status: 'draft',
      year: currentYear,
      updatedAt: serverTimestamp(),
    };

    const docRef = doc(this.db, 'offsite', this.firestoreDocId);
    setDoc(docRef, payload, { merge: true })
      .then(() => {
        this.isSaving = false;
        this.toastr.success('Draft saved to database successfully!');
      })
      .catch((err) => {
        console.error('Firestore save error:', err);
        this.isSaving = false;
        this.toastr.error('Failed to save draft. Please try again.');
      });
  }

  loadSavedData(): void {
    if (!this.employeeInfo.code) return;
    this.firestoreDocId = this.employeeInfo.code;
    const docRef = doc(this.db, 'offsite', this.firestoreDocId);

    getDoc(docRef)
      .then((snap) => {
        if (!snap.exists()) {
          this.isLoadingFormData = false;
          return;
        }
        const data = snap.data();

        // Track submitted status (disable form after restoring values)
        const wasSubmitted = data['status'] === 'submitted';

        // Restore form values
        this.offsiteForm.patchValue({
          attendance: data['attendance'] || '',
          mealPreference: data['mealPreference'] || '',
          aadharNumber: data['aadharNumber']
            ? data['aadharNumber'].replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')
            : '',
          roomAllocation: data['roomAllocation'] || 'triple',
          departureCity: data['departureCityRaw'] || '',
          departureCityOther: data['departureCityOther'] || '',
          returnCity: data['returnCityRaw'] || '',
          returnCityOther: data['returnCityOther'] || '',
        });
        if (!this.isLoadingConfig) {
          this.resetUnavailableRoomAllocation();
        }

        // Restore file references
        const fileNames: string[] = data['uploadedFileNames'] || [];
        const fileURLs: string[] = data['uploadedFileURLs'] || [];
        if (fileNames.length > 0) {
          this.uploadedFiles = fileNames.map((name: string, i: number) => {
            const ext = (name.split('.').pop() || '').toLowerCase();
            const fileType = ['jpg', 'jpeg', 'png'].includes(ext) ? 'image' : 'pdf';
            return {
              file: null,
              displayName: name,
              downloadURL: fileURLs[i] || '',
              fileType,
              previewURL: ''
            };
          });
          this.offsiteForm.patchValue({ idDocument: fileNames });
        }

        // Restore employee info from Firestore
        this.employeeInfo = {
          name: data['employeeName'] || this.employeeInfo.name,
          code: data['employeeCode'] || this.employeeInfo.code,
          email: data['employeeEmail'] || this.employeeInfo.email,
          contact: data['employeeContact'] || this.employeeInfo.contact,
          department: data['employeeDepartment'] || this.employeeInfo.department,
          gender: data['employeeGender'] || this.employeeInfo.gender,
          company: this.employeeInfo.company || data['employeeCompany'] || '',
          category: data['employeeCategory'] || this.employeeInfo.category,
        };

        // Trigger conditional sections
        if (data['attendance'] === 'Yes') {
          this.onAttendanceChange('Yes');
        }

        // Disable form AFTER restoring all values so patchValue works
        if (wasSubmitted) {
          this.isSubmitted = true;
          this.offsiteForm.disable();
        }

        const status = data['status'] === 'submitted' ? 'Submitted form' : 'Draft';
        this.toastr.info(`${status} loaded from database`);
        this.isLoadingFormData = false;
      })
      .catch((err) => {
        console.error('Firestore load error:', err);
        this.isLoadingFormData = false;
      });
  }

  validateForm(): boolean {
    if (this.timerExpired) {
      this.showReadOnlyWarning();
      return false;
    }

    if (!this.offsiteForm.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.offsiteForm.controls).forEach(key => {
        this.offsiteForm.get(key)?.markAsTouched();
      });
      
      this.toastr.error('Please fill all required fields');
      return false;
    }
    
    // Additional validation for "No" response
    if (this.offsiteForm.value.attendance === 'No') {
      return true;
    }
    
    // Validate file upload
    if (this.uploadedFiles.length === 0 && this.offsiteForm.value.attendance === 'Yes') {
      this.toastr.error('Please upload your ID document');
      return false;
    }
    
    return true;
  }

  submitForm(): void {
    if (this.isFormReadOnly) {
      this.showReadOnlyWarning();
      return;
    }

    if (!this.validateForm()) {
      return;
    }
    
    this.isSubmitting = true;
    this.firestoreDocId = this.employeeInfo.code || 'unknown';
    const currentYear = new Date().getFullYear().toString();

    // Upload files to Firebase Storage, then save document
    const uploadPromises: Promise<{ displayName: string; downloadURL: string }>[] = [];

    if (this.offsiteForm.value.attendance === 'Yes') {
      for (const f of this.uploadedFiles) {
        if (f.downloadURL) {
          // Already uploaded (auto-upload on select or previous draft)
          uploadPromises.push(Promise.resolve({ displayName: f.displayName, downloadURL: f.downloadURL }));
        } else if (f.file) {
          // Fallback: upload now if somehow not yet uploaded
          const storagePath = `offsite/${currentYear}/${this.employeeInfo.code}/submission/${f.displayName}`;
          const storageRef = ref(this.storage, storagePath);
          const promise = uploadBytes(storageRef, f.file)
            .then(snapshot => getDownloadURL(snapshot.ref))
            .then(url => ({ displayName: f.displayName, downloadURL: url }));
          uploadPromises.push(promise);
        }
      }
    }

    Promise.all(uploadPromises)
      .then((uploadedResults) => {
        const payload: Record<string, any> = {
          // Employee info
          employeeCode: this.employeeInfo.code,
          employeeName: this.employeeInfo.name,
          employeeEmail: this.employeeInfo.email,
          employeeContact: this.employeeInfo.contact,
          employeeDepartment: this.employeeInfo.department,
          employeeGender: this.employeeInfo.gender,
          employeeCompany: this.employeeInfo.company,
          employeeCategory: this.employeeInfo.category,

          // Form fields
          attendance: this.offsiteForm.value.attendance,
          mealPreference: this.offsiteForm.value.mealPreference || '',
          aadharNumber: this.offsiteForm.value.aadharNumber || '',
          roomAllocation: this.offsiteForm.value.roomAllocation || '',
          departureCity: this.offsiteForm.value.departureCity === 'Other'
            ? this.offsiteForm.value.departureCityOther || ''
            : this.offsiteForm.value.departureCity || '',
          returnCity: this.offsiteForm.value.returnCity === 'Other'
            ? this.offsiteForm.value.returnCityOther || ''
            : this.offsiteForm.value.returnCity || '',
          departureCityRaw: this.offsiteForm.value.departureCity || '',
          departureCityOther: this.offsiteForm.value.departureCityOther || '',
          returnCityRaw: this.offsiteForm.value.returnCity || '',
          returnCityOther: this.offsiteForm.value.returnCityOther || '',

          // Uploaded files
          uploadedFileNames: uploadedResults.map(r => r.displayName),
          uploadedFileURLs: uploadedResults.map(r => r.downloadURL),

          // Status
          status: 'submitted',
          year: currentYear,
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        const docRef = doc(this.db, 'offsite', this.firestoreDocId);
        return setDoc(docRef, payload, { merge: true });
      })
      .then(() => {
        this.isSubmitting = false;
        this.isSubmitted = true;
        this.offsiteForm.disable();
        this.toastr.success('Form submitted successfully!');
      })
      .catch((err) => {
        console.error('Submit error:', err);
        this.isSubmitting = false;
        this.toastr.error('Submission failed. Please try again.');
      });
  }

  trackByCity(index: number, city: string): string {
    return city;
  }

  private hasPositiveAmount(amount: number | null | undefined): boolean {
    const numericAmount = Number(amount || 0);
    return Number.isFinite(numericAmount) && numericAmount > 0;
  }

  private markDeadlinePassed(): void {
    this.timerExpired = true;
    this.timerDisplay = '00d 00h 00m 00s';
    this.timerUrgent = false;

    if (this.offsiteForm && !this.offsiteForm.disabled) {
      this.offsiteForm.disable();
    }
  }

  private showReadOnlyWarning(): void {
    if (this.isSubmitted) {
      this.toastr.warning('This registration has already been submitted.');
      return;
    }

    this.toastr.warning('The registration deadline has passed. The form is read-only.');
  }

  private resetUnavailableRoomAllocation(): void {
    const selectedRoomAllocation = this.offsiteForm?.get('roomAllocation')?.value;
    const isUnavailableDouble = selectedRoomAllocation === 'double' && !this.showDoubleSharingOption;
    const isUnavailableSingle = selectedRoomAllocation === 'single' && !this.showSingleSharingOption;

    if (isUnavailableDouble || isUnavailableSingle) {
      this.offsiteForm.get('roomAllocation')?.setValue('triple');
    }
  }

  onAadharKeypress(event: KeyboardEvent): boolean {
    return /[0-9]/.test(event.key);
  }

  onAadharInput(event: any): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 12);
    // Format as XXXX XXXX XXXX
    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    input.value = formatted;
    // Store formatted value in the form control so display stays consistent
    this.offsiteForm.get('aadharNumber')?.setValue(formatted, { emitEvent: false });
    this.offsiteForm.get('aadharNumber')?.markAsTouched();
  }

  onAadharPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 12);
    const formatted = pasted.replace(/(\d{4})(?=\d)/g, '$1 ');
    const input = event.target as HTMLInputElement;
    input.value = formatted;
    this.offsiteForm.get('aadharNumber')?.setValue(formatted, { emitEvent: false });
    this.offsiteForm.get('aadharNumber')?.markAsTouched();
  }

  trackByFile(index: number, file: { file: File | null; displayName: string }): string {
    return file.displayName;
  }
}
